import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { FaListOl } from 'react-icons/fa';

import '../Button/NeonButton.css';
import './MultiplayerResultModal.css';
import WarpLink from '../WarpTransition/WarpLink';
import { lockBodyScroll } from '../../utils/bodyScrollLock';

const CELEBRATION_PARTICLES = Array.from({ length: 18 }, (_, index) => index);

const MultiplayerResultModal = ({
  isOpen,
  isTie,
  kicker,
  headline,
  winningNumber,
  tieWinners = [],
  primaryLabel,
  primaryTo,
  primaryTransitionLabel,
  primaryTransitionVariant,
  onPrimary,
  onClose,
}) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const releaseScrollLock = lockBodyScroll();
    dialogRef.current?.focus();

    return () => {
      releaseScrollLock();
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && !isTie) {
      onClose();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = dialogRef.current?.querySelectorAll(
      'a[href], button:not([disabled])'
    );
    if (!focusableElements?.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="multiplayerResultModal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.section
            ref={dialogRef}
            className={`multiplayerFinalCall multiplayerResultModal__dialog ${
              isTie
                ? 'multiplayerFinalCall--tie'
                : 'multiplayerFinalCall--winner'
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="multiplayer-result-title"
            aria-describedby="multiplayer-result-score"
            tabIndex="-1"
            onKeyDown={handleKeyDown}
            initial={{ opacity: 0, scale: 0.68, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.82, y: 24 }}
            transition={{ type: 'spring', stiffness: 220, damping: 17 }}
          >
            {CELEBRATION_PARTICLES.map((particle) => (
              <span
                key={particle}
                className="multiplayerFinalCall__particle"
                style={{
                  '--particle-left': `${4 + (particle * 92) / 17}%`,
                  '--particle-delay': `${(particle % 6) * 0.08}s`,
                  '--particle-tilt': `${particle * 23}deg`,
                }}
                aria-hidden="true"
              />
            ))}

            <p className="multiplayerFinalCall__kicker">{kicker}</p>
            <h2
              id="multiplayer-result-title"
              className="multiplayerFinalCall__headline"
            >
              {headline}
            </h2>
            <p
              id="multiplayer-result-score"
              className="multiplayerFinalCall__score"
            >
              {isTie ? 'Locked at ' : 'Winning roll '}
              <strong>{winningNumber}</strong>
            </p>

            {isTie && (
              <ul
                className="multiplayerFinalCall__names"
                aria-label="Tied contenders"
              >
                {tieWinners.map((winner, index) => (
                  <li
                    className="multiplayerFinalCall__contender"
                    key={winner.id}
                  >
                    <span
                      className="multiplayerFinalCall__contenderIndex"
                      aria-hidden="true"
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <strong className="multiplayerFinalCall__contenderName">
                      {winner.name}
                    </strong>
                    <span
                      className="multiplayerFinalCall__contenderSignal"
                      aria-hidden="true"
                    />
                  </li>
                ))}
              </ul>
            )}

            <div className="multiplayerResultModal__actions">
              <WarpLink
                className="neonButton neonButton--primary multiplayerResultModal__button multiplayerResultModal__button--primary"
                to={primaryTo}
                label={primaryTransitionLabel}
                variant={primaryTransitionVariant}
                onBeforeNavigate={onPrimary}
              >
                <>
                  <svg
                    className="multiplayerResultModal__launchIcon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M13.5 2 4 14h7l-1 8 10-13h-7z" />
                  </svg>
                  <span className="multiplayerResultModal__launchCopy">
                    <span
                      className="multiplayerResultModal__launchSignal"
                      aria-hidden="true"
                    >
                      {isTie ? 'SUDDEN DEATH // READY' : 'ARENA // READY'}
                    </span>
                    <span className="multiplayerResultModal__launchLabel">
                      {primaryLabel}
                    </span>
                  </span>
                  <span
                    className="multiplayerResultModal__launchArrow"
                    aria-hidden="true"
                  >
                    &#187;
                  </span>
                </>
              </WarpLink>
              {!isTie && (
                <button
                  type="button"
                  className="neonButton neonButton--muted multiplayerResultModal__button multiplayerResultModal__button--secondary"
                  onClick={onClose}
                >
                  <FaListOl className="neonButton__icon" aria-hidden="true" />
                  <span className="neonButton__label">
                    close &amp; view rolls
                  </span>
                </button>
              )}
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

MultiplayerResultModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isTie: PropTypes.bool.isRequired,
  kicker: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  winningNumber: PropTypes.number.isRequired,
  tieWinners: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  primaryLabel: PropTypes.string.isRequired,
  primaryTo: PropTypes.string.isRequired,
  primaryTransitionLabel: PropTypes.string.isRequired,
  primaryTransitionVariant: PropTypes.oneOf(['roll', 'danger', 'portal'])
    .isRequired,
  onPrimary: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MultiplayerResultModal;
